'use client';
import {Variant} from "@/types";
import ResponsiveVariantCard from "@/components/variants/responsive-variant-card";

interface InitialStaticGridProps {
    variants: Variant[];
    /**
     * How many cards mark themselves priority for image preload. Default 2 —
     * the smallest-common-denominator across breakpoints (Mobile 2-col → 2,
     * Desktop 6-col → still only 2 LCP-relevant). Override only if you
     * specifically need eager loading on more cards (rare).
     */
    priorityCount?: number;
    /**
     * Cap on how many cards to render. Default 48 (max initial page size on
     * category pages). Not-found / empty-state pages typically pass fewer.
     */
    maxCards?: number;
}

/**
 * Pure-CSS variant grid used as:
 *   1. Pre-hydration SSR fallback inside the virtualized variant-grid.tsx
 *      — renders before the virtualizer measures container width.
 *   2. Standalone display on not-found pages and fallback grids where
 *      virtualization is overkill.
 *
 * Uses the .initial-static-grid CSS class defined in globals.css with
 * media-keyed --card-min variables that match the virtualizer's per-breakpoint
 * minWidth thresholds (160 / 200 / 220 px). This keeps SSR and post-hydration
 * column counts in sync — preventing CLS on Mobile during hydrate.
 */
export default function InitialStaticGrid({
    variants,
    priorityCount = 2,
    maxCards = 48,
}: InitialStaticGridProps) {
    return (
        <div className="grid gap-1 w-full initial-static-grid">
            {variants.slice(0, maxCards).map((variant, index) => (
                <div key={variant.variant_id || index} className="aspect-[1/1.4]">
                    <ResponsiveVariantCard
                        variant={variant}
                        priority={index < priorityCount}
                    />
                </div>
            ))}
        </div>
    );
}
